import CustomTooltip from '../../shared/CustomTooltip';
import EditIcon from '@mui/icons-material/Edit';
import {
    Card,
    CardActionArea,
    Chip,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetFeeListQuery } from '../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../shared/AuthRoles';
import { camelCaseToTitleCase, capitalizeName } from '../../../shared/utils';
import moment from 'moment';
import { ExperienceType, FeeType } from '../../enums/experience';
import { FeeSortKey, TypeFilter } from './FeesToolbar';

export interface Fee {
    id: number;
    name: string;
    moduleTypes: ExperienceType[];
    createdAt: Date;
    type: FeeType;
    active?: boolean;
}

export default function FeesList({
    query,
    typeFilter,
    expFilter,
    sortKey,
}: {
    query: string;
    typeFilter: TypeFilter;
    expFilter: ExperienceType[];
    sortKey: FeeSortKey;
}) {
    const navigate = useNavigate();
    const loggedInUser = UserRole();
    const { data: getFees } = useGetFeeListQuery(loggedInUser?.company);
    const fees: Fee[] = getFees ?? [];

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();

        let rows = fees.filter((f) => {
            const matchesQ =
                !q ||
                f.name.toLowerCase().includes(q) ||
                camelCaseToTitleCase(f.type).toLowerCase().includes(q);

            const matchesType = typeFilter === 'All' || f.type === typeFilter;

            const matchesExp =
                expFilter.length === 0 ||
                f.moduleTypes.length === 0 || // fee applies to all
                f.moduleTypes.some((t) => expFilter.includes(t));

            return matchesQ && matchesType && matchesExp;
        });

        rows = rows.sort((a, b) => {
            if (sortKey === 'nameAsc') return a.name.localeCompare(b.name);
            if (sortKey === 'nameDesc') return b.name.localeCompare(a.name);
            if (sortKey === 'createdDesc')
                return (
                    new Date(b.createdAt).getTime() -
                    new Date(a.createdAt).getTime()
                );
            if (sortKey === 'createdAsc')
                return (
                    new Date(a.createdAt).getTime() -
                    new Date(b.createdAt).getTime()
                );
            return 0;
        });

        return rows;
    }, [fees, query, typeFilter, expFilter, sortKey]);

    if (filtered.length === 0) {
        return (
            <Typography sx={{ mt: 3 }}>
                No results. Try adjusting your filters.
            </Typography>
        );
    }

    return (
        // Stack width ensures list aligns with toolbar container width
        <Stack spacing={1.5} sx={{ width: '100%' }}>
            {filtered.map((fee) => (
                <FeeRow
                    key={fee.id}
                    fee={fee}
                    onClick={() => navigate(`/fees/${fee.id}/edit`)}
                />
            ))}
        </Stack>
    );
}

// ------- Row card -------
function FeeRow({ fee, onClick }: { fee: Fee; onClick: () => void }) {
    return (
        <Card
            variant='outlined'
            sx={{
                borderRadius: 2,
                '&:hover': { borderColor: 'primary.light', boxShadow: 1 },
            }}
        >
            {/* px=1.5 to visually align with toolbar inputs/gutters */}
            <CardActionArea onClick={onClick} sx={{ px: 1.5, py: 1.5 }}>
                <Stack
                    direction='row'
                    alignItems='center'
                    spacing={2}
                    sx={{ width: '100%' }}
                >
                    {/* Left */}
                    <Stack sx={{ minWidth: 220 }}>
                        <Typography variant='caption' sx={{ color: 'primary' }}>
                            {capitalizeName(fee.name)}
                        </Typography>
                        <Typography variant='body3' color='text.primary'>
                            Fee Id #{fee.id} Created{' '}
                            {moment(fee.createdAt).format('DD/MM/YYYY')}
                        </Typography>
                    </Stack>

                    {/* Middle meta */}
                    <Stack
                        direction='row'
                        spacing={1}
                        alignItems='center'
                        sx={{ minWidth: 150, overflow: 'hidden' }}
                    >
                        <Chip
                            size='small'
                            label={camelCaseToTitleCase(fee.type)}
                            variant='outlined'
                            sx={{
                                minWidth: 80, // adjust to whatever looks balanced
                                justifyContent: 'center', // keep text centered inside
                            }}
                        />
                    </Stack>
                    <Stack
                        direction='row'
                        spacing={1}
                        alignItems='center'
                        sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}
                    >
                        <Typography variant='body3' color='text.primary' noWrap>
                            Applies to:&nbsp;
                            {fee.moduleTypes.length === 0
                                ? 'All'
                                : fee.moduleTypes
                                      .map((t) => capitalizeName(String(t)))
                                      .join(', ')}
                        </Typography>
                    </Stack>

                    {/* Right actions (don’t bubble) */}
                    <Stack
                        direction='row'
                        spacing={1}
                        alignItems='center'
                        onClick={(e) => e.stopPropagation()}
                    >
                        <CustomTooltip title='Edit'>
                            <IconButton size='small' onClick={onClick}>
                                <EditIcon fontSize='small' />
                            </IconButton>
                        </CustomTooltip>
                    </Stack>
                </Stack>
            </CardActionArea>
        </Card>
    );
}
